import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
class ErrorLoggerService {
    constructor(config = {}) {
        this.logs = [];
        this.batchQueue = [];
        this.batchTimeout = null;
        this.config = {
            maxEntries: 1000,
            enableConsoleLogging: true,
            enableRemoteLogging: false,
            remoteEndpoint: '',
            apiKey: '',
            batchSize: 10,
            batchTimeout: 5000,
            enableUserContext: true,
            ...config
        };
        this.sessionId = this.generateSessionId();
        this.setupGlobalErrorHandlers();
    }
    generateSessionId() {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    setupGlobalErrorHandlers() {
        // Handle unhandled errors
        window.addEventListener('error', (event) => {
            this.logError(new Error(event.message), undefined, 'error', {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                source: 'global'
            });
        });
        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.logError(new Error(`Unhandled Promise Rejection: ${event.reason}`), undefined, 'error', {
                reason: event.reason,
                source: 'promise'
            });
        });
    }
    logError(error, errorInfo, level = 'error', context) {
        const entry = {
            id: this.generateErrorId(),
            timestamp: Date.now(),
            error,
            errorInfo,
            userAgent: navigator.userAgent,
            url: window.location.href,
            userId: this.getUserId(),
            sessionId: this.sessionId,
            level,
            context,
            stackTrace: error.stack
        };
        // Add to local storage
        this.logs.push(entry);
        // Maintain max entries limit
        if (this.logs.length > this.config.maxEntries) {
            this.logs = this.logs.slice(-this.config.maxEntries);
        }
        // Console logging
        if (this.config.enableConsoleLogging) {
            this.logToConsole(entry);
        }
        // Remote logging
        if (this.config.enableRemoteLogging) {
            this.queueForRemoteLogging(entry);
        }
        // Store in localStorage for persistence
        this.persistToLocalStorage();
    }
    generateErrorId() {
        return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    getUserId() {
        // Try to get user ID from various sources
        try {
            // Check localStorage
            const user = localStorage.getItem('user');
            if (user) {
                return JSON.parse(user).id;
            }
            // Check sessionStorage
            const sessionUser = sessionStorage.getItem('user');
            if (sessionUser) {
                return JSON.parse(sessionUser).id;
            }
        }
        catch {
            // Ignore parsing errors
        }
        return undefined;
    }
    logToConsole(entry) {
        const logMethod = entry.level === 'error' ? console.error :
            entry.level === 'warning' ? console.warn : console.log;
        logMethod(`[ErrorLogger] ${entry.level.toUpperCase()}:`, {
            id: entry.id,
            timestamp: new Date(entry.timestamp).toISOString(),
            error: entry.error,
            errorInfo: entry.errorInfo,
            context: entry.context,
            sessionId: entry.sessionId,
            userId: entry.userId
        });
    }
    queueForRemoteLogging(entry) {
        this.batchQueue.push(entry);
        // Send immediately if batch is full
        if (this.batchQueue.length >= this.config.batchSize) {
            this.flushBatch();
        }
        else {
            // Schedule batch send
            if (this.batchTimeout) {
                clearTimeout(this.batchTimeout);
            }
            this.batchTimeout = setTimeout(() => {
                this.flushBatch();
            }, this.config.batchTimeout);
        }
    }
    async flushBatch() {
        if (this.batchQueue.length === 0 || !this.config.remoteEndpoint) {
            return;
        }
        const batch = [...this.batchQueue];
        this.batchQueue = [];
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
            this.batchTimeout = null;
        }
        try {
            const response = await fetch(this.config.remoteEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.config.apiKey && { 'Authorization': `Bearer ${this.config.apiKey}` })
                },
                body: JSON.stringify({
                    errors: batch.map(entry => ({
                        ...entry,
                        error: {
                            name: entry.error.name,
                            message: entry.error.message,
                            stack: entry.error.stack
                        }
                    }))
                })
            });
            if (!response.ok) {
                throw new Error(`Remote logging failed: ${response.status}`);
            }
            if (this.config.enableConsoleLogging) {
                console.log(`[ErrorLogger] Successfully sent ${batch.length} errors to remote endpoint`);
            }
        }
        catch (error) {
            // Re-queue failed entries
            this.batchQueue.unshift(...batch);
            if (this.config.enableConsoleLogging) {
                console.error('[ErrorLogger] Failed to send errors to remote endpoint:', error);
            }
        }
    }
    persistToLocalStorage() {
        try {
            const recentLogs = this.logs.slice(-50); // Only store last 50 entries
            localStorage.setItem('errorLogger_logs', JSON.stringify(recentLogs));
        }
        catch {
            // Ignore localStorage errors
        }
    }
    loadFromLocalStorage() {
        try {
            const stored = localStorage.getItem('errorLogger_logs');
            if (stored) {
                const logs = JSON.parse(stored);
                this.logs = logs;
            }
        }
        catch {
            // Ignore parsing errors
        }
    }
    getLogs(level, limit) {
        let filteredLogs = this.logs;
        if (level) {
            filteredLogs = this.logs.filter(log => log.level === level);
        }
        if (limit) {
            filteredLogs = filteredLogs.slice(-limit);
        }
        return filteredLogs.sort((a, b) => b.timestamp - a.timestamp);
    }
    getLogsByTimeRange(startTime, endTime) {
        return this.logs.filter(log => log.timestamp >= startTime && log.timestamp <= endTime).sort((a, b) => b.timestamp - a.timestamp);
    }
    clearLogs() {
        this.logs = [];
        localStorage.removeItem('errorLogger_logs');
    }
    getSessionErrors() {
        return this.logs.filter(log => log.sessionId === this.sessionId);
    }
    exportLogs(format = 'json') {
        if (format === 'csv') {
            const headers = ['ID', 'Timestamp', 'Level', 'Error Message', 'URL', 'User ID', 'Session ID'];
            const rows = this.logs.map(log => [
                log.id,
                new Date(log.timestamp).toISOString(),
                log.level,
                log.error.message,
                log.url,
                log.userId || '',
                log.sessionId
            ]);
            return [headers, ...rows].map(row => row.join(',')).join('\n');
        }
        return JSON.stringify(this.logs, null, 2);
    }
    // React hook for using error logger
    static useErrorLogger() {
        const logError = React.useCallback((error, errorInfo, level, context) => {
            errorLogger.logError(error, errorInfo, level, context);
        }, []);
        const getLogs = React.useCallback((level, limit) => {
            return errorLogger.getLogs(level, limit);
        }, []);
        return { logError, getLogs };
    }
}
// Global instance
export const errorLogger = new ErrorLoggerService();
// React Hook
export const useErrorLogger = ErrorLoggerService.useErrorLogger;
// HOC for automatic error logging
export function withErrorLogging(Component, componentName) {
    const WrappedComponent = (props) => {
        const { logError } = useErrorLogger();
        React.useEffect(() => {
            const originalConsoleError = console.error;
            console.error = (...args) => {
                // Check if this looks like a React error
                if (args.length > 0 && typeof args[0] === 'string' && args[0].includes('Error:')) {
                    logError(new Error(args.join(' ')), undefined, 'error', {
                        component: componentName || Component.displayName || Component.name,
                        source: 'console.error'
                    });
                }
                originalConsoleError(...args);
            };
            return () => {
                console.error = originalConsoleError;
            };
        }, [logError]);
        return _jsx(Component, { ...props });
    };
    WrappedComponent.displayName = `withErrorLogging(${componentName || Component.displayName || Component.name})`;
    return WrappedComponent;
}
